import { Link, useNavigate } from 'react-router-dom';
import { Button, Alert } from '@dreamtree-org/twreact-ui';
import { Plus } from 'lucide-react';
import { PageHeader, StatCard, SectionCard } from '@/components';
import { formatINR } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import { useVendorSession } from '../VendorApp';

/** Vendor overview: what needs doing (orders to ship, products in review) and earnings. */
export default function Home() {
  const nav = useNavigate();
  const { vendor } = useVendorSession();
  const earnings = useQuery('vendorEarnings', undefined, { select: (r) => r.data });
  const products = useQuery('vendorProducts', {}, { select: (r) => r.data || [] });
  const toShip = useQuery('vendorOrders', { status: 'to_ship' }, { select: (r) => r.data || [] });
  const e = earnings.data || {};
  const p = products.data || [];
  const count = (s) => p.filter((x) => x.review_status === s).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome, ${vendor.contact_name || vendor.business_name}`}
        subtitle="List products for the store to review, ship the orders customers place, and track what you earn."
        actions={<Button onClick={() => nav('/products?new=1')} leftIcon={<Plus size={16} />}>Add product</Button>}
      />
      {(toShip.data || []).length ? (
        <Alert variant="warning" title={`${toShip.data.length} item${toShip.data.length === 1 ? '' : 's'} to ship`}>
          Customers are waiting. <Link className="underline font-medium" to="/orders">Ship them now</Link>.
        </Alert>
      ) : null}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Live products" value={p.filter((x) => x.live).length} hint={`${p.length} listed in all`} />
        <StatCard label="Awaiting review" value={count('pending')} hint={count('rejected') ? `${count('rejected')} rejected` : undefined} />
        <StatCard label="Sales" value={formatINR(e.sales_paise || 0)} hint={`${e.lines || 0} items sold`} />
        <StatCard label="Your earnings" value={formatINR(e.earning_paise || 0)} hint={`After ${formatINR(e.commission_paise || 0)} store commission`} />
      </div>
      <SectionCard title="How payouts work">
        <p className="text-sm opacity-80">
          Each sale records the store's commission and your share at the moment the order is placed. You have earned
          {' '}<span className="font-semibold">{formatINR(e.delivered_earning_paise || 0)}</span> on delivered items; the store settles with you directly.
        </p>
      </SectionCard>
    </div>
  );
}
